import React, { useState } from 'react';
import {
  Card,
  CardBody,
  CardHeader,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Progress,
  Table,
  Row,
  Col
} from 'reactstrap';
import axios from 'axios';

const ImportTemplate = ({ onSuccess }) => {
  const [file, setFile] = useState(null);
  const [name, setName] = useState('');
  const [version, setVersion] = useState('1.0.0');
  const [framework, setFramework] = useState('FedRAMP');
  const [baseline, setBaseline] = useState('Moderate');
  const [importing, setImporting] = useState(false);
  const [progress, setProgress] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      // Auto-fill name from filename if empty
      if (!name) {
        const filename = selectedFile.name.replace(/\.[^/.]+$/, ''); // Remove extension
        setName(filename);
      }
      setResult(null);
      setError(null);
    }
  };

  const handleImport = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a DOCX file to import');
      return;
    }

    if (!name) {
      setError('Please enter a template name');
      return;
    }

    const formData = new FormData();
    formData.append('templateFile', file);
    formData.append('name', name);
    formData.append('version', version);
    formData.append('framework', framework);
    formData.append('baseline', baseline);

    try {
      setImporting(true);
      setProgress(30);
      setError(null);

      const response = await fetch(`${baseURL}/ssp-studio/templates/import-docx`, {
        method: 'POST',
        body: formData,
        credentials: 'include'
      });

      setProgress(70);

      const data = await response.json();

      if (data.success) {
        setProgress(100);
        setResult(data.data);
        // Reset form
        setFile(null);
        setName('');
        setVersion('1.0.0');
        document.getElementById('templateFileInput').value = '';

        // Call parent callback if provided
        if (onSuccess) {
          onSuccess(data.data.template);
        }
      } else {
        setError(data.message || 'Failed to import template');
      }
    } catch (err) {
      setError(err.message || 'An error occurred during import');
    } finally {
      setImporting(false);
      setTimeout(() => setProgress(0), 1000);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader>
          <i className="mdi mdi-file-import"></i> Import Template from DOCX
        </CardHeader>
        <CardBody>
          <Alert color="info">
            <strong>How it works:</strong>
            <ul className="mb-0 mt-2">
              <li>Upload a Microsoft Word (.docx) file</li>
              <li>Sections are automatically extracted based on Heading styles (H1-H6)</li>
              <li>Supports section numbering like "2.1 Title" or "Section 2.1: Title"</li>
              <li>After import, you can publish the template to make it available for SSP creation</li>
            </ul>
          </Alert>

          <Form onSubmit={handleImport}>
            <FormGroup>
              <Label for="templateFileInput">DOCX File *</Label>
              <Input
                type="file"
                id="templateFileInput"
                onChange={handleFileChange}
                disabled={importing}
                accept=".docx"
              />
              <small className="form-text text-muted">
                Microsoft Word document (.docx) - max 50MB
              </small>
            </FormGroup>

            {file && (
              <Alert color="info" className="mt-3">
                <strong>Selected:</strong> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </Alert>
            )}

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="templateName">Template Name *</Label>
                  <Input
                    type="text"
                    id="templateName"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    disabled={importing}
                    placeholder="e.g., FedRAMP Moderate SSP Template"
                  />
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="templateVersion">Version</Label>
                  <Input
                    type="text"
                    id="templateVersion"
                    value={version}
                    onChange={(e) => setVersion(e.target.value)}
                    disabled={importing}
                    placeholder="e.g., 1.0.0"
                  />
                </FormGroup>
              </Col>
            </Row>

            <Row>
              <Col md={6}>
                <FormGroup>
                  <Label for="framework">Framework</Label>
                  <Input
                    type="select"
                    id="framework"
                    value={framework}
                    onChange={(e) => setFramework(e.target.value)}
                    disabled={importing}
                  >
                    <option value="FedRAMP">FedRAMP</option>
                    <option value="NIST 800-53">NIST 800-53</option>
                    <option value="NIST CSF">NIST CSF</option>
                    <option value="ISO 27001">ISO 27001</option>
                    <option value="CMMC">CMMC</option>
                    <option value="StateRAMP">StateRAMP</option>
                    <option value="Generic">Generic</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md={6}>
                <FormGroup>
                  <Label for="baseline">Baseline</Label>
                  <Input
                    type="select"
                    id="baseline"
                    value={baseline}
                    onChange={(e) => setBaseline(e.target.value)}
                    disabled={importing}
                  >
                    <option value="Low">Low</option>
                    <option value="Moderate">Moderate</option>
                    <option value="High">High</option>
                    <option value="N/A">N/A</option>
                  </Input>
                </FormGroup>
              </Col>
            </Row>

            {importing && (
              <div className="mb-3">
                <Progress value={progress} animated />
                <p className="text-center text-muted mt-2">
                  Importing template and extracting sections...
                </p>
              </div>
            )}

            {error && (
              <Alert color="danger" toggle={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Button
              type="submit"
              color="primary"
              disabled={!file || !name || importing}
              block
            >
              {importing ? 'Importing...' : 'Import Template'}
            </Button>
          </Form>
        </CardBody>
      </Card>

      {result && (
        <Card className="mt-3">
          <CardHeader className="bg-success text-white">
            <i className="mdi mdi-check-circle"></i> Template Imported Successfully
          </CardHeader>
          <CardBody>
            <Row>
              <Col md={6}>
                <h6>Template Information</h6>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td><strong>Name:</strong></td>
                      <td>{result.template.name}</td>
                    </tr>
                    <tr>
                      <td><strong>Version:</strong></td>
                      <td>{result.template.version}</td>
                    </tr>
                    <tr>
                      <td><strong>Status:</strong></td>
                      <td>
                        <span className="badge bg-warning">Draft</span>
                      </td>
                    </tr>
                    <tr>
                      <td><strong>Sections Extracted:</strong></td>
                      <td>{result.parseStats.sectionsExtracted}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
              <Col md={6}>
                <h6>Extraction Details</h6>
                <Table size="sm" borderless>
                  <tbody>
                    <tr>
                      <td><strong>Source File:</strong></td>
                      <td>{result.parseStats.sourceFile}</td>
                    </tr>
                    <tr>
                      <td><strong>File Size:</strong></td>
                      <td>{(result.parseStats.fileSize / 1024 / 1024).toFixed(2)} MB</td>
                    </tr>
                    <tr>
                      <td><strong>Warnings:</strong></td>
                      <td>{result.parseStats.warnings || 0}</td>
                    </tr>
                  </tbody>
                </Table>
              </Col>
            </Row>

            <Alert color="warning" className="mt-3">
              <i className="mdi mdi-information"></i>
              <strong> Next Steps:</strong>
              <ul className="mb-0 mt-2">
                <li>Review the extracted sections in the Templates tab</li>
                <li>Click "Publish" to make this template available for SSP creation</li>
                <li>Published templates can be selected when creating a new SSP</li>
              </ul>
            </Alert>
          </CardBody>
        </Card>
      )}
    </div>
  );
};

export default ImportTemplate;
